<template>
  <div class="receipt__modal" :class="{ 'is-center ': showHeader }">
    <dtt-title tag="h2" class="receipt__modal-title" v-if="showHeader">
      {{ title }}
    </dtt-title>
    <p class="receipt__modal-text">
      {{ receipt.legalPersonName || taxpayer.name }},
      {{ $t(`${TRANSLATION_READ_PATH}.tin`) }}
      {{ receipt.tin || taxpayer?.tin }}
    </p>
    <p class="receipt__modal-text receipt__modal-address">
      {{ receipt?.crData?.locationName }},
      {{ receipt?.crData?.locationAddress }}
    </p>

    <div v-if="isAgentReceipt(receipt)" class="receipt__modal-text">
      {{ $t(`${TRANSLATION_READ_PATH}.supplier.label`) }}:
      {{
        joinStrings([
          receipt.legalPersonSupplierName,
          $t(`${TRANSLATION_READ_PATH}.supplier.tin`) +
            ' ' +
            receipt.supplierTin,
        ])
      }}
    </div>

    <!-- Ошибки -->
    <div v-if="criticalErrors.length">
      <h3 class="receipt-errors__header">
        <span
          class="receipt-errors__counter receipt-errors__counter_critical m_r-3"
          >{{ criticalErrors.length }}</span
        >
        {{ $t(`${TRANSLATION_READ_PATH}.errors.critical-error`) }}
      </h3>
      <ul class="receipt-errors__list">
        <li v-for="error of criticalErrors" class="small_text" :key="error.id">
          <span class="semi_bold">{{
            $t(`${TRANSLATION_READ_PATH}.errors.code`, {
              errorCode: error.code,
            })
          }}</span>
          <span>{{ error.description }}</span>
        </li>
      </ul>
    </div>

    <div
      v-if="acceptableErrors.length"
      class="receipt-errors receipt-errors__container receipt-errors__container_non-critical d-flex flex-column"
    >
      <h3 class="receipt-errors__header">
        <span
          class="receipt-errors__counter receipt-errors__counter_non-critical m_r-3"
          >{{ acceptableErrors.length }}</span
        >
        {{ $t(`${TRANSLATION_READ_PATH}.errors.non-critical-error`) }}
      </h3>
      <ul class="receipt-errors__list">
        <li
          v-for="error of acceptableErrors"
          class="small_text"
          :key="error.id"
        >
          <span class="semi_bold">{{
            $t(`${TRANSLATION_READ_PATH}.errors.code`, {
              errorCode: error.code,
            })
          }}</span>
          <span>{{ error.description }}</span>
        </li>
      </ul>
    </div>

    <FormattedDate
      :date="receipt.dateTime || new Date()"
      formatType="dd MMM yyyy"
      class="receipt__modal-text"
    />

    <div class="receipt__modal-personal-info">
      <div class="receipt__modal-personal-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.receipt`) }}
        </span>
        <span class="receipt__modal-item-desc">
          #{{ receipt.ticketNumber }}
        </span>
      </div>
      <div class="receipt__modal-personal-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.shift`) }}
        </span>
        <span class="receipt__modal-item-desc">
          #{{ receipt?.crData?.shiftNumber }}
        </span>
      </div>
      <div class="receipt__modal-personal-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.cashier`) }}
        </span>
        <span class="receipt__modal-item-desc">
          {{ receipt?.crData?.cashierName }}
        </span>
      </div>
    </div>
    <div class="receipt__modal-detail-info">
      <div class="receipt__modal-detail-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.cashRegisterId`) }}
        </span>
        <span class="receipt__modal-item-desc">
          {{ receipt.crRegisterNumber || '-' }}
        </span>
      </div>
      <div class="receipt__modal-detail-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.fiscalMemoryDevice`) }}
        </span>
        <span class="receipt__modal-item-desc">
          {{ receipt.fnSerialNumber || '-' }}
        </span>
      </div>
      <div class="receipt__modal-detail-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.fiscalDocument`) }}
        </span>
        <span class="receipt__modal-item-desc">
          {{ isShowFdNumber ? receipt.fdNumber : '–' }}
        </span>
      </div>
      <div class="receipt__modal-detail-info-item">
        <span class="receipt__modal-item-title">
          {{ $t(`${TRANSLATION_READ_PATH}.fiscalMark`) }}
        </span>
        <span class="receipt__modal-item-desc">
          {{ receipt.documentFiscalMark || '-' }}
        </span>
      </div>
    </div>

    <div v-if="isCorrection">
      <dtt-title tag="h3">
        {{
          receipt.correction.type === correctionReceiptTypes.MANUAL
            ? $t(`${TRANSLATION_READ_PATH}.manualCorrection`)
            : $t(`${TRANSLATION_READ_PATH}.onRequestCorrection`)
        }}
      </dtt-title>
      <div class="receipt__modal-personal-info">
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{ $t(`${TRANSLATION_READ_PATH}.incidentDate`) }}
          </span>
          <span class="receipt__modal-item-desc">
            <FormattedDate
              :date="receipt.correction.date"
              formatType="dd MMM yyyy"
              class="receipt__modal-text"
            />
          </span>
        </div>
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{ $t(`${TRANSLATION_READ_PATH}.payment_type`) }}
          </span>
          <span class="receipt__modal-item-desc">
            {{ paymentType }}
          </span>
        </div>
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{
              $t(
                receipt?.correction?.type === correctionReceiptTypes.ON_REQUEST
                  ? `${TRANSLATION_READ_PATH}.reason`
                  : `${TRANSLATION_READ_PATH}.reasonManual`,
              )
            }}
          </span>
          <span class="receipt__modal-item-desc">
            {{ receipt.correction.reason || '-' }}
          </span>
        </div>
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{ $t(`${TRANSLATION_READ_PATH}.fiscalDocument`) }}
          </span>
          <span class="receipt__modal-item-desc">
            {{ receipt.originFdNumber }}
          </span>
        </div>
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{ $t(`${TRANSLATION_READ_PATH}.fiscalMemoryDevice`) }}
          </span>
          <span class="receipt__modal-item-desc">
            {{ receipt.originFnSerialNumber }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="isReturn && receipt.originFdNumber && receipt.originFnSerialNumber"
    >
      <h3 class="m_b-2">{{ $t(`${TRANSLATION_READ_PATH}.origin-receipt`) }}</h3>
      <div class="receipt__modal-personal-info">
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{ $t(`${TRANSLATION_READ_PATH}.fiscalDocument`) }}
          </span>
          <span class="receipt__modal-item-desc">
            {{ receipt.originFdNumber }}
          </span>
        </div>
        <div class="receipt__modal-personal-info-item">
          <span class="receipt__modal-item-title">
            {{ $t(`${TRANSLATION_READ_PATH}.fiscalMemoryDevice`) }}
          </span>
          <span class="receipt__modal-item-desc">
            {{ receipt.originFnSerialNumber }}
          </span>
        </div>
      </div>
    </div>

    <dtt-table :data="receipt.items" header-size="sm" cell-size="sm">
      <dtt-table-column
        header="№"
        width="20%"
        prop="goodName"
        cell-divider
        header-divider
        headerClass="accordion__header"
      >
        <template #body="{ index }">
          {{ index + 1 }}
        </template>
      </dtt-table-column>
      <dtt-table-column
        :header="$t(`${TRANSLATION_READ_PATH}.item`)"
        prop="goodName"
        cell-divider
        header-divider
        headerClass="accordion__header"
      >
        <template
          #body="{
            dataItem: {
              goodName,
              goodVatCode,
              goodStCode,
              sgtin,
              goodNomenclatureCode,
            },
          }"
        >
          <div class="small_text text-break">{{ goodName }}</div>
          <div class="small_text">
            {{ $t(`${TRANSLATION_READ_PATH}.vat`) }}
            {{ getTaxRate(goodVatCode, 'VAT') }}%,
            {{ $t(`${TRANSLATION_READ_PATH}.st`) }}
            {{ getTaxRate(goodStCode, 'ST') }}%
          </div>
          <div
            class="small_text"
            v-if="!!(sgtin || checkGoodNomenclatureCode(goodNomenclatureCode))"
          >
            <div>
              [{{ sgtin || checkGoodNomenclatureCode(goodNomenclatureCode) }}]
            </div>
          </div>
        </template>
      </dtt-table-column>
      <dtt-table-column
        :header="$t(`${TRANSLATION_READ_PATH}.price`)"
        prop="goodPrice"
        cell-divider
        header-divider
        headerClass="accordion__header"
        width="40%"
      >
        <template #body="{ dataItem: { goodPrice } }">
          <FormattedMoney :number="goodPrice || 0" />
        </template>
      </dtt-table-column>
      <dtt-table-column
        :header="$t(`${TRANSLATION_READ_PATH}.qty`)"
        prop="goodQuantity"
        cell-divider
        header-divider
        headerClass="accordion__header"
        width="40%"
      />
      <dtt-table-column
        :header="$t(`${TRANSLATION_READ_PATH}.total`)"
        prop="goodCost"
        cell-divider
        header-divider
        headerClass="accordion__header"
        width="40%"
      >
        <template #body="{ dataItem: { goodCost } }">
          <FormattedMoney :number="goodCost || 0" />
        </template>
      </dtt-table-column>
    </dtt-table>
    <div>
      <div class="receipt__modal-footer-head">
        <p class="receipt__modal-footer-title">
          {{ $t(`${TRANSLATION_READ_PATH}.${paymentTypeMarkKey}`) }}
        </p>
        <p class="receipt__modal-footer-desc">
          {{ $t(`${TRANSLATION_READ_PATH}.tax_system`) }}: {{ taxSystem }}
        </p>
      </div>

      <div class="receipt__modal-footer-list">
        <div class="receipt__modal-footer-item">
          <span class="receipt__modal-footer-desc">
            {{ $t(`${TRANSLATION_READ_PATH}.subtotal`) }}
          </span>
          <span class="receipt__modal-footer-desc">
            <FormattedMoney :number="receipt.subtotal || 0" />
          </span>
        </div>
        <div
          class="receipt__modal-footer-item"
          v-for="tax in taxCounters"
          :key="tax.code"
        >
          <span class="receipt__modal-footer-desc">
            {{
              tax.type === taxTypes.VAT
                ? $t(`${TRANSLATION_READ_PATH}.vat`)
                : $t(`${TRANSLATION_READ_PATH}.st`)
            }}
            {{
              getTaxRate(tax.code, tax.type === taxTypes.VAT ? 'VAT' : 'ST')
            }}%
          </span>
          <span class="receipt__modal-footer-desc">
            <FormattedMoney :number="tax.sum || 0" />
          </span>
        </div>
      </div>
      <div class="receipt__modal-footer-item">
        <dtt-title tag="h4">
          {{ $t(`${TRANSLATION_READ_PATH}.total`) }}
        </dtt-title>
        <dtt-title tag="h4">
          <FormattedMoney :number="receipt.ticketTotalSum || 0" />
        </dtt-title>
      </div>

      <!-- Аванс -->
      <div
        class="receipt__modal-footer-item"
        v-if="receipt.ticketPrePaymentSum"
      >
        <span class="receipt__modal-footer-desc">
          {{ $t(`${TRANSLATION_READ_PATH}.advance`) }}
        </span>
        <span class="receipt__modal-footer-desc">
          <FormattedMoney :number="receipt.ticketPrePaymentSum || 0" />
        </span>
      </div>

      <!-- Кредит -->
      <div
        class="receipt__modal-footer-item"
        v-if="receipt.ticketPostPaymentSum"
      >
        <span class="receipt__modal-footer-desc">
          {{ $t(`${TRANSLATION_READ_PATH}.credit`) }}
        </span>
        <span class="receipt__modal-footer-desc">
          <FormattedMoney :number="receipt.ticketPostPaymentSum || 0" />
        </span>
      </div>

      <!-- Received -->

      <div
        class="receipt__modal-footer-item"
        v-if="
          (receipt.ticketPrePaymentSum || receipt.ticketPostPaymentSum) &&
          received > 0
        "
      >
        <span class="receipt__modal-footer-desc">
          {{ $t(`${TRANSLATION_READ_PATH}.received`) }}
        </span>
        <span class="receipt__modal-footer-desc">
          <FormattedMoney :number="received || 0" />
        </span>
      </div>

      <!-- Тип платежа -->
      <div class="receipt__modal-footer-item" v-if="paymentType">
        <span class="receipt__modal-footer-desc">
          {{ $t(`${TRANSLATION_READ_PATH}.payment_type`) }}
        </span>
        <span class="receipt__modal-footer-desc">
          {{ paymentType }}
        </span>
      </div>

      <div class="qr_container">
        <qrcode-vue :value="qrUrl" :size="256" level="M" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop } from 'vue-property-decorator';
import { PropType } from 'vue';
import {
  IDetailedReceipt,
  IFormatAndLogicControlError,
  IReceipt,
  IReceiptItem,
  ITaxpayer,
  ITaxRate,
} from '@/libs/interfaces';
import {
  CalculationTypesCodes,
  CorrectionReceiptTypes,
  FormatAndLogicControlErrorsSeverityType,
  PaymentTypeMarks,
  SyntheticReceiptTypes,
  TaxTypes,
  BackendReceiptTypes,
} from '@/libs/enums';
import { getReceiptType } from '@/utils';
import { isEmpty, sumBy } from 'lodash';
import services from '@/api/services';
import stores from '@/store/core/RootStore';
import FormattedDate from '@/components/FormattedDate.vue';
import FormattedMoney from '@/components/FormattedMoney.vue';
import QrcodeVue from 'qrcode.vue';

interface IDetailedReceiptWithSubtotal extends IDetailedReceipt {
  subtotal: number;
}

@Component({
  name: 'ReceiptDetails',
  components: { FormattedDate, FormattedMoney, QrcodeVue },
})
export default class ReceiptDetails extends Vue {
  taxSystemsServices = stores.taxSystemStore;
  dictionaries = stores.businessObjectsDictionaryStore;
  @Prop({
    type: String,
  })
  id!: string;

  @Prop({
    type: Boolean,
    default: false,
  })
  showHeader!: boolean;

  @Prop({
    type: Object as PropType<ITaxpayer>,
  })
  taxpayer!: ITaxpayer;

  @Prop({
    type: Boolean,
  })
  isGuest!: boolean;

  @Prop({
    type: Boolean,
  })
  withoutSidebar!: boolean;
  @Prop({
    type: Array as PropType<{ id: string; sn: string }[]>,
  })
  cashRegisters!: { id: string; sn: string }[];
  @Prop({
    type: Boolean,
  })
  isShowFdNumber!: boolean;

  public readonly TRANSLATION_READ_PATH = 'components.receipt-details';
  isLoading = false;
  isOpened = false;
  notFound = false;
  receipt: IDetailedReceiptWithSubtotal = {
    subtotal: 0,
    cashierName: '',
    items: [],
    locationAddress: '',
    locationName: '',
    legalPersonName: '',
    legalPersonSupplierName: '',
    shiftId: '',
    crData: {
      cashierName: '',
      locationAddress: '',
      locationName: '',
      shiftNumber: 0,
    },
    originFdNumber: 0,
    originFnSerialNumber: '',
    correction: {
      date: '',
      reason: '',
      type: 0,
    },
    dataFormatVersion: 0,
    dateTime: '',
    documentFiscalMark: '',
    fdNumber: 0,
    fnSerialNumber: '',
    id: '',
    crRegisterNumber: '',
    operationType: 0,
    shiftNumber: 0,
    supplierTin: '',
    taxCounters: [],
    taxSystem: 0,
    ticketNumber: 0,
    ticketTotalCashSum: 0,
    ticketTotalCashlessSum: 0,
    ticketPrePaymentSum: 0,
    ticketPostPaymentSum: 0,
    ticketTotalSum: 0,
    type: BackendReceiptTypes.REGISTRATION_REPORT,
    tin: '',
    errors: {},
  };

  taxTypes = TaxTypes;
  correctionReceiptTypes = CorrectionReceiptTypes;
  _taxRates: ITaxRate[] = [];
  qrUrl = '';

  get isCorrection(): boolean {
    return (
      this.receipt &&
      (this.receiptType === SyntheticReceiptTypes.CORRECTION_INCOME ||
        this.receiptType === SyntheticReceiptTypes.CORRECTION_INCOME_RETURN ||
        this.receiptType === SyntheticReceiptTypes.CORRECTION_EXPENDITURE ||
        this.receiptType ===
          SyntheticReceiptTypes.CORRECTION_EXPENDITURE_RETURN)
    );
  }

  get receiptType(): SyntheticReceiptTypes {
    return getReceiptType(this.receipt)!;
  }

  get isReturn(): boolean {
    return (
      this.receiptType === SyntheticReceiptTypes.EXPENDITURE_RETURN ||
      this.receiptType === SyntheticReceiptTypes.INCOME_RETURN
    );
  }

  get title(): string {
    switch (this.receiptType) {
      case SyntheticReceiptTypes.INCOME:
        return this.translate('receipt-title.receiptHeaderIncome');
      case SyntheticReceiptTypes.INCOME_RETURN:
        return this.translate('receipt-title.receiptHeaderIncomeReturn');
      case SyntheticReceiptTypes.EXPENDITURE:
        return this.translate('receipt-title.receiptHeaderExpense');
      case SyntheticReceiptTypes.EXPENDITURE_RETURN:
        return this.translate('receipt-title.receiptHeaderExpenseReturn');
      case SyntheticReceiptTypes.CORRECTION_INCOME:
        return this.translate('receipt-title.receiptHeaderCorrectionIncome');
      case SyntheticReceiptTypes.CORRECTION_INCOME_RETURN:
        return this.translate(
          'receipt-title.receiptHeaderCorrectionIncomeReturn',
        );
      case SyntheticReceiptTypes.CORRECTION_EXPENDITURE:
        return this.translate('receipt-title.receiptHeaderCorrectionExpense');
      case SyntheticReceiptTypes.CORRECTION_EXPENDITURE_RETURN:
        return this.translate(
          'receipt-title.receiptHeaderCorrectionExpenseReturn',
        );
      default:
        return '';
    }
  }

  get received(): number {
    return (
      this.receipt.ticketTotalCashSum || this.receipt.ticketTotalCashlessSum
    );
  }

  get paymentType() {
    if (this.receipt.ticketTotalCashSum > 0) {
      return this.translate('cash');
    }

    if (this.receipt.ticketTotalCashlessSum > 0) {
      return this.translate('cashless');
    }

    return '';
  }

  get paymentTypeMarkKey(): string | null {
    if (
      this.receipt == null ||
      this.receipt.items == null ||
      this.receipt.items.length == 0
    ) {
      return null;
    }

    const paymentTypeMark = this.receipt.items[0].paymentType;
    switch (paymentTypeMark) {
      case PaymentTypeMarks.FullSettlement:
        return 'payment-mark.full-settlement';
      case PaymentTypeMarks.Advance:
        return 'payment-mark.advance';
      case PaymentTypeMarks.PartialSettlementWithCredit:
        return 'payment-mark.partial-settlement-with-credit';
      case PaymentTypeMarks.Credit:
        return 'payment-mark.credit';
      case PaymentTypeMarks.CreditSettlement:
        return 'payment-mark.credit-settlement';
      default:
        return null;
    }
  }

  get taxSystem(): string {
    const taxSystem = this.taxSystems.find(
      (t: any) => t.code === this.receipt.taxSystem,
    );
    if (taxSystem) {
      return taxSystem.taxName;
    }

    return '';
  }

  get taxCounters() {
    return this.receipt.taxCounters == null
      ? []
      : this.receipt.taxCounters.sort((a, b) =>
          ('' + b.type).localeCompare(a.type),
        );
  }

  private get allErrors(): IFormatAndLogicControlError[] {
    if (this.receipt.errors == null) {
      return [];
    }

    const errorCodes = Object.keys(this.receipt.errors)
      .map((errorCode: string) => errorCode.replace('error_', ''))
      .map((errorCode: string) => Number(errorCode));

    const errors = this.dictionaries.formatAndLogicControlErrors.filter(
      (error: IFormatAndLogicControlError) => errorCodes.includes(error.code),
    );

    return errors;
  }

  public get criticalErrors(): IFormatAndLogicControlError[] {
    return this.allErrors.filter(
      (error: IFormatAndLogicControlError) =>
        error.type === FormatAndLogicControlErrorsSeverityType.CRITICAL,
    );
  }

  public get acceptableErrors(): IFormatAndLogicControlError[] {
    return this.allErrors.filter(
      (error: IFormatAndLogicControlError) =>
        error.type === FormatAndLogicControlErrorsSeverityType.ACCEPTABLE,
    );
  }

  public isAgentReceipt(receipt: IReceipt): boolean {
    return !isEmpty(receipt.supplierTin);
  }

  public isAgentReceiptAsPrincipal(receipt: IReceipt): boolean {
    return (
      !isEmpty(receipt.supplierTin) && receipt.supplierTin !== this.taxpayer.tin
    );
  }

  public isAgentReceiptAsAgent(receipt: IReceipt): boolean {
    return (
      !isEmpty(receipt.supplierTin) && receipt.supplierTin === this.taxpayer.tin
    );
  }

  public isCurrencyReceiptItem(receiptItem: IReceiptItem): boolean {
    if (receiptItem == null) {
      return false;
    }

    return (
      receiptItem.calcAttributeCode === CalculationTypesCodes.BankingServices ||
      receiptItem.calcAttributeCode ===
        CalculationTypesCodes.ForeignCurrencyExchange
    );
  }

  checkGoodNomenclatureCode(code: string) {
    return code && code.includes('\u0000') ? null : code;
  }

  async loadData(id: string): Promise<void> {
    this.isOpened = true;
    this.isLoading = true;
    this.notFound = false;
    try {
      const { data } = await services.receiptService.getTaxRates();
      this._taxRates = data;
      const { data: receiptInfodata } =
        await services.receiptService.getReceipt(id, this.isGuest);
      this.receipt = this.transformReceipt(receiptInfodata);
      this.qrUrl = this.generateUrl(receiptInfodata);
    } catch (err) {
      console.error(err);
      this.notFound = true;
    } finally {
      this.isLoading = false;
    }
  }

  private transformReceipt(
    receipt: IDetailedReceipt,
  ): IDetailedReceiptWithSubtotal {
    const taxes: number = sumBy(receipt.taxCounters, (i) => i.sum);
    const subtotal = receipt.ticketTotalSum - taxes;
    receipt.items?.forEach((item) => {
      if (
        item.goodNomenclatureCode != null &&
        item.goodNomenclatureCode.startsWith('444D')
      ) {
        item.sgtin = this.hex2ascii(item.goodNomenclatureCode.substr(16));
      }
    });
    return {
      ...receipt,
      subtotal,
    };
  }

  private translate(key: string): string {
    return this.$tc(`${this.TRANSLATION_READ_PATH}.${key}`);
  }

  private hex2ascii(hex: any): string {
    if (!(typeof hex === 'number' || typeof hex === 'string')) {
      return '';
    }
    hex = hex.toString().replace(/\s+/gi, '');
    const stack = [];
    for (let i = 0; i < hex.length; i += 2) {
      const code = parseInt(hex.substr(i, 2), 16);
      if (!isNaN(code) && code !== 0) {
        stack.push(String.fromCharCode(code));
      }
    }
    return stack.join('');
  }

  getTaxRate(code: number, type: string): number | string {
    const rateValue = this._taxRates.find(
      (taxRate) => taxRate.code === code && taxRate.taxRateType === type,
    )?.taxRateValue;
    return rateValue !== undefined ? rateValue : '';
  }

  getSerialNumber(regNumber: string) {
    return this.cashRegisters?.find((cr) => cr.id === regNumber)?.sn || null;
  }

  get taxSystems() {
    return this.taxSystemsServices.taxSystems;
  }

  get taxServiceWebsite() {
    const url = new URL('https://sti.gov.kg/');
    return {
      link: url.toString(),
      name: url.hostname,
    };
  }

  private generateUrl(receipt: IDetailedReceipt) {
    const taxServiceLink = `${process.env.VUE_APP_TAX_SERVICE_LINK}/ticket`;
    const url = new URL(taxServiceLink);

    url.searchParams.append(
      'date',
      receipt.dateTime.replace(/-/g, '').replace(/:/g, '').replace(/Z/g, ''),
    );
    url.searchParams.append('type', String(receipt.type));
    url.searchParams.append('operation_type', String(receipt.operationType));
    url.searchParams.append('fn_number', String(receipt.fnSerialNumber));
    url.searchParams.append('fd_number', String(receipt.fdNumber));
    url.searchParams.append('fm', String(receipt.documentFiscalMark));
    url.searchParams.append('tin', String(receipt.tin));
    url.searchParams.append('regNumber', String(receipt.crRegisterNumber));
    url.searchParams.append('sum', String(receipt.ticketTotalSum));

    return url.toString();
  }

  public joinStrings(values: string[]): string {
    return values
      .filter((value: string) => value != null && value.trim() !== '')
      .join(', ');
  }

  mounted() {
    this.loadData(this.id);
  }
}
</script>
<style lang="scss" scoped>
.is-center {
  text-align: center;
}
.receipt__modal {
  &-title {
    display: flex;
    justify-content: center;
  }
  &-text {
    @include f14;
    color: '#434A53';
    margin-bottom: 0.8rem;
  }

  &-address {
    margin-bottom: 3.2rem;
  }

  &-item-title {
    @include f14;
    font-weight: 500;
    color: #939aa4;
  }

  &-item-desc {
    @include f14;
    font-weight: 600;
    color: #59606a;
  }

  &-personal-info {
    @include flex-row(5rem);
    margin-bottom: 3.2rem;

    &-item {
      @include flex-row(0.8rem);
    }
  }

  &-detail-info {
    @include flex-row(3.2rem);
    margin-bottom: 3.2rem;
    &-item {
      @include flex-column(0.4rem);
    }
  }

  &-footer {
    &-head {
      margin-bottom: 0.8rem;
      text-align: start;
    }

    &-list {
      margin-bottom: 1.6rem;
    }
    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.2rem;
    }

    &-title {
      @include f16;
      color: #474d55;
    }

    &-desc {
      @include f14;
      font-weight: 400;
      color: #474d55;
    }
  }
}

.qr_container {
  display: flex;
  justify-content: center;
  margin-top: 4rem;
}
</style>
